import { Controller } from "@hotwired/stimulus"

// Handles the AI document import UX:
//   - Drag-and-drop file zone (with click-to-browse fallback)
//   - Tab switching between "Upload file" and "Paste text" modes
//   - Live filename display after selection
export default class extends Controller {
  static targets = [
    "fileInput",   // hidden <input type="file">
    "dropZone",    // the visual drop area div
    "fileLabel",   // shows selected filename
    "fileName",    // selected filename
    "fileSize",    // selected file size
    "fileError",   // client-side validation message
    "fileSubmitBtn",
    "fileSubmitLabel",
    "filePane",    // upload form pane
    "textPane",    // paste text form pane
    "fileTab",     // "Upload file" tab button
    "textTab",     // "Paste text" tab button
    "textArea",    // the paste textarea
    "submitBtn",   // text-mode submit button
  ]

  static allowedExtensions = ["pdf", "csv", "xlsx", "txt"]
  static maxFileSize = 10 * 1024 * 1024

  connect() {
    this._dragging = 0  // counter to handle enter/leave across child elements
  }

  // ── Tab switching ───────────────────────────────────────────────────

  showFileTab(e) {
    e.preventDefault()
    this.filePaneTarget.classList.remove("hidden")
    this.textPaneTarget.classList.add("hidden")
    this._setActiveTab(this.fileTabTarget, this.textTabTarget)
  }

  showTextTab(e) {
    e.preventDefault()
    this.textPaneTarget.classList.remove("hidden")
    this.filePaneTarget.classList.add("hidden")
    this._setActiveTab(this.textTabTarget, this.fileTabTarget)
  }

  _setActiveTab(active, inactive) {
    active.classList.add("border-brand-500", "text-brand-700")
    active.classList.remove("border-transparent", "text-gray-500")
    inactive.classList.remove("border-brand-500", "text-brand-700")
    inactive.classList.add("border-transparent", "text-gray-500")
  }

  // ── Drop zone ───────────────────────────────────────────────────────

  dragenter(e) {
    e.preventDefault()
    this._dragging++
    this.dropZoneTarget.classList.add("border-brand-400", "bg-brand-50")
  }

  dragover(e) {
    e.preventDefault()  // required to allow drop
  }

  dragleave(e) {
    this._dragging--
    if (this._dragging <= 0) {
      this._dragging = 0
      this.dropZoneTarget.classList.remove("border-brand-400", "bg-brand-50")
    }
  }

  drop(e) {
    e.preventDefault()
    this._dragging = 0
    this.dropZoneTarget.classList.remove("border-brand-400", "bg-brand-50")

    const files = e.dataTransfer?.files
    if (files?.length > 0) {
      this._selectFile(files[0], files)
    }
  }

  openFilePicker(e) {
    e?.preventDefault()
    this.fileInputTarget.click()
  }

  fileSelected(e) {
    const file = e.target.files?.[0]
    if (file) this._selectFile(file)
  }

  clearFile(e) {
    e.preventDefault()
    this.fileInputTarget.value = ""
    this.fileLabelTarget.classList.add("hidden")
    this.fileLabelTarget.classList.remove("flex")
    this.fileErrorTarget.classList.add("hidden")
    this.fileErrorTarget.textContent = ""
    this.fileSubmitBtnTarget.disabled = true
  }

  submitting() {
    if (!this.hasFileSubmitBtnTarget || this.fileSubmitBtnTarget.disabled) return

    this.fileSubmitBtnTarget.disabled = true
    if (this.hasFileSubmitLabelTarget) this.fileSubmitLabelTarget.textContent = "Uploading…"
  }

  _selectFile(file, fileList = null) {
    const error = this._validationError(file)
    if (error) {
      this.fileInputTarget.value = ""
      this.fileLabelTarget.classList.add("hidden")
      this.fileLabelTarget.classList.remove("flex")
      this.fileSubmitBtnTarget.disabled = true
      this.fileErrorTarget.textContent = error
      this.fileErrorTarget.classList.remove("hidden")
      return
    }

    if (fileList) this.fileInputTarget.files = fileList
    this.fileErrorTarget.classList.add("hidden")
    this.fileErrorTarget.textContent = ""
    this._updateLabel(file.name, file.size)
    this.fileSubmitBtnTarget.disabled = false
  }

  _validationError(file) {
    const extension = file.name.split(".").pop()?.toLowerCase()
    if (!this.constructor.allowedExtensions.includes(extension)) {
      return "Unsupported file type. Choose a PDF, XLSX, CSV, or TXT file."
    }
    if (file.size > this.constructor.maxFileSize) {
      return "This file is larger than 10 MB. Choose a smaller file."
    }
    return null
  }

  _updateLabel(name, size) {
    const kb = (size / 1024).toFixed(0)
    const display = size >= 1024 * 1024
      ? `${(size / (1024 * 1024)).toFixed(1)} MB`
      : `${kb} KB`
    this.fileNameTarget.textContent = name
    this.fileSizeTarget.textContent = display
    this.fileLabelTarget.classList.remove("hidden")
    this.fileLabelTarget.classList.add("flex")
  }

  // ── Text area — enable submit only when non-empty ───────────────────

  textInput(e) {
    if (!this.hasSubmitBtnTarget) return
    const empty = e.target.value.trim().length === 0
    this.submitBtnTarget.disabled = empty
  }
}
